/**
 * Payment Gateway v1 — pulling the stable error code out of a refusal.
 *
 * The wire contract (`docs/payment-module/gateway-api-contract.md` §1) is a CLOSED set of
 * machine-stable codes; the accompanying `message` is an English developer diagnostic and
 * must never reach a user. Text mapping lives in `./errorText.js` (`gatewayErrorText`,
 * contract §9) — this module only extracts the code, and stays dependency-free (no axios
 * import) so it is trivially usable from hooks, components and tests alike.
 */

/**
 * Pull the stable `code` out of an axios-shaped rejection, matching the
 * `{"detail": {"code", "message"}}` envelope every gateway endpoint refuses with.
 * Returns null when the shape doesn't match (network error, non-gateway 4xx/5xx).
 */
export function extractGatewayErrorCode(e) {
  const detail = e?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) return detail.code || null;
  if (typeof detail === "string") return detail;
  return null;
}

/**
 * Pull the FIELD POINTERS out of a refusal: `[{field, rule, ...context}]`.
 *
 * `SETTINGS_INVALID` covers thirty-three settings fields and a dozen commission-rule
 * shapes, so the code alone can only ever say "these settings aren't valid" — which is
 * what the operator saw with two bad fields on screen and no way to tell which. The
 * backend answers a closed `rule` vocabulary per offending field
 * (`modules/gateway/settings_policy.RULE_NAMES`) precisely so the console can name them in
 * the operator's own language; the server's English `message` still never renders.
 *
 * Returns `[]` for every refusal that is not about a field, so a caller can render
 * unconditionally.
 */
export function extractGatewayErrorFields(e) {
  const detail = e?.response?.data?.detail;
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return [];
  const fields = detail.fields;
  if (!Array.isArray(fields)) return [];
  return fields.filter((f) => f && typeof f === "object" && typeof f.field === "string");
}

export default extractGatewayErrorCode;
